// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { UserRole } from '../types';

export interface RoleConfig {
  permissions: string[];
  visibleRoutes: string[];
  canSeeAllSites: boolean;
  isReadOnly: boolean;
}

/**
 * Static role → permission/route map derived from ADR-0001 and the IAM OpenAPI spec.
 * Used by the frontend to enforce role-based visibility in mock mode.
 */
export const ROLE_PERMISSIONS: Record<UserRole, RoleConfig> = {
  admin: {
    permissions: [
      'iam.user.read',
      'iam.user.create',
      'iam.user.update',
      'iam.user.delete',
      'iam.role.read',
      'iam.role-assignment.read',
      'iam.role-assignment.create',
      'iam.role-assignment.delete',
      'twin.site.read',
      'twin.entity.read',
      'twin.model.read',
      'objectstore.object.read',
      'objectstore.object.create',
      'objectstore.object.list',
      'audit.log.read',
      'weather.forecast.read',
      'weather.historical.read',
    ],
    visibleRoutes: [
      '/',
      '/home',
      '/alarms/:id',
      '/site/:site_id',
      '/log',
      '/audit',
      '/prefs',
      '/fleet',
      '/optimizations',
      '/onboarding',
      '/bills',
      '/users',
      '/access',
      '/admin/agent',
      '/search',
    ],
    canSeeAllSites: true,
    isReadOnly: false,
  },

  operations_manager: {
    permissions: [
      'twin.site.read',
      'twin.entity.read',
      'twin.model.read',
      'iam.user.read',
      'iam.role.read',
      'iam.role-assignment.read',
      'objectstore.object.read',
      'objectstore.object.create',
      'objectstore.object.list',
      'audit.log.read',
      'weather.forecast.read',
      'weather.historical.read',
    ],
    visibleRoutes: [
      '/',
      '/home',
      '/alarms/:id',
      '/site/:site_id',
      '/log',
      '/audit',
      '/prefs',
      '/fleet',
      '/optimizations',
      '/onboarding',
      '/bills',
      '/search',
    ],
    canSeeAllSites: true,
    isReadOnly: false,
  },

  operator: {
    permissions: [
      'twin.site.read',
      'twin.entity.read',
      'objectstore.object.read',
      'objectstore.object.list',
    ],
    visibleRoutes: ['/', '/home', '/alarms/:id', '/site/:site_id', '/log', '/audit', '/prefs'],
    canSeeAllSites: false,
    isReadOnly: false,
  },

  executive: {
    permissions: [
      'twin.site.read',
      'twin.entity.read',
      'twin.model.read',
      'iam.user.read',
      'audit.log.read',
    ],
    visibleRoutes: ['/', '/executive', '/bills', '/prefs'],
    canSeeAllSites: true,
    isReadOnly: true,
  },
};

/**
 * Role hierarchy for "minimum role" checks.
 * Executive is orthogonal (read-only across all scopes).
 */
const ROLE_HIERARCHY: UserRole[] = ['operator', 'operations_manager', 'admin'];

/**
 * Check if a role meets or exceeds a required minimum role.
 * Executive is handled specially — it only matches itself.
 */
export function meetsMinimumRole(userRole: UserRole, requiredRole: UserRole): boolean {
  if (requiredRole === 'executive' || userRole === 'executive') {
    return userRole === requiredRole;
  }
  const userLevel = ROLE_HIERARCHY.indexOf(userRole);
  const requiredLevel = ROLE_HIERARCHY.indexOf(requiredRole);
  if (userLevel === -1 || requiredLevel === -1) return false;
  return userLevel >= requiredLevel;
}
